/**
 * ESS POS — Stage 5: Manager Workspace
 *
 * Implements:
 * - Manager Dashboard (Daily revenue, completed orders, avg transaction value, pending orders,
 *   sales by payment method, visual sales performance chart, low-stock alerts, staff activity)
 * - Discounts, Voids, Order Cancellations & Refunds Approval Queue
 * - Sales Analytics, Order Management & Daily/Monthly CSV Reports
 * - Cashier & Waiter Performance Oversight
 * - Inventory Valuation & Product Performance (Live GET /api/v1/reports/inventory-valuation)
 * - Staff Management (Live GET/POST /api/v1/tenants/staff) & Audit History
 */

import { apiClient } from '../api/apiClient';
import { restaurantMockAdapter } from '../api/mockAdapter';
import { openFastSwitchStaffModal, openLockStationModal } from './waiterWorkspace';
import {
  escapeHtml,
  exportRowsToCsv,
  formatCurrency,
  formatDateTime,
  openModal,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  showToast,
} from '../components/ui';
import {
  BranchRecord,
  DailySalesReport,
  InventoryBalanceRecord,
  InventoryValuationReport,
  ProductRecord,
  SaleRecord,
} from '../types/contracts';

export async function renderManagerWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  viewport.innerHTML = renderLoadingState('Loading Manager Operational & Financial Controls...');

  try {
    const [dailySales, valuation, sales, balances, products, staff, branches] = await Promise.all([
      apiClient.getDailySalesReport(),
      apiClient.getInventoryValuationReport(),
      apiClient.listSales(undefined, 50),
      apiClient.getInventoryBalances(),
      apiClient.listProducts(),
      apiClient.listStaff(),
      apiClient.listBranches(),
    ]);

    const tenantId = apiClient.getEffectiveTenantId();
    const orders = restaurantMockAdapter.listOrders(tenantId);
    const approvals = restaurantMockAdapter.listApprovals(tenantId);
    const auditLogs = restaurantMockAdapter.listAuditLogs(tenantId);
    const user = apiClient.getUser();
    const managerName = `${user?.firstName || 'Sarah'} ${user?.lastName || 'Achieng'}`.trim();

    const refresh = () => renderManagerWorkspace(viewport, activeTab, onNavigateTab);

    if (activeTab === 'approvals') {
      renderManagerApprovalsScreen(viewport, {
        tenantId,
        managerName,
        approvals,
        onRefresh: refresh,
      });
    } else if (activeTab === 'orders-sales') {
      renderOrdersAndSalesAnalyticsScreen(viewport, {
        dailySales,
        sales,
        orders,
      });
    } else if (activeTab === 'staff-performance') {
      renderStaffPerformanceScreen(viewport, {
        staff,
        sales,
        orders,
      });
    } else if (activeTab === 'inventory-reports') {
      renderInventoryAndProductReportsScreen(viewport, {
        valuation,
        balances,
        products,
        sales,
      });
    } else if (activeTab === 'staff-audit') {
      renderStaffAndAuditScreen(viewport, {
        staff,
        branches,
        auditLogs,
        onRefresh: refresh,
      });
    } else if (activeTab === 'profile') {
      renderManagerProfileScreen(viewport, {
        dailySales,
        sales,
        staff,
        branches,
        onRefresh: refresh,
        onNavigateTab,
      });
    } else {
      // Default: 'overview'
      renderManagerOverviewDashboard(viewport, {
        dailySales,
        sales,
        orders,
        balances,
        approvals,
        auditLogs,
        onNavigateTab,
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load Manager Workspace',
      message: err?.message || 'Unable to fetch management reports.',
      retryButtonId: 'mgr-retry-btn',
    });
    viewport.querySelector('#mgr-retry-btn')?.addEventListener('click', () => {
      renderManagerWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

// ============================================================================
// 1. MANAGER DASHBOARD (BUSINESS OVERVIEW)
// ============================================================================

function renderManagerOverviewDashboard(
  viewport: HTMLElement,
  params: {
    dailySales: DailySalesReport;
    sales: SaleRecord[];
    orders: any[];
    balances: InventoryBalanceRecord[];
    approvals: any[];
    auditLogs: any[];
    onNavigateTab: (tab: string) => void;
  }
) {
  const rev = params.dailySales?.totalRevenueCents ?? 0;
  const txCount = params.dailySales?.transactionCount ?? 0;
  const avgTxCents = txCount > 0 ? Math.round(rev / txCount) : 0;
  const pendingOrders = params.orders.filter(
    (o) => o.status !== 'PAID' && o.status !== 'CANCELLED'
  );
  const pendingApprovals = params.approvals.filter((a) => a.status === 'PENDING');
  const lowStockItems = params.balances.filter(
    (b) => Number(b.quantity_on_hand) <= Number(b.reorder_threshold)
  );

  const cashCents = params.dailySales?.revenueBreakdown?.cashCents ?? 0;
  const mpesaCents = params.dailySales?.revenueBreakdown?.mpesaCents ?? 0;
  const otherCents = params.dailySales?.revenueBreakdown?.otherCents ?? 0;

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Manager Operational &amp; Financial Dashboard</h2>
          <p class="ess-section-desc">Real-time restaurant revenue, gross profit margin, staff activity &amp; pending approvals</p>
        </div>
        <div class="ess-btn-group">
          <button type="button" class="ess-btn ess-btn-secondary" id="mgr-go-reports">Sales Analytics</button>
          <button type="button" class="ess-btn ess-btn-primary" id="mgr-go-approvals">
            Pending Approvals (${pendingApprovals.length})
          </button>
        </div>
      </div>

      <!-- KPI Row -->
      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Total Daily Revenue</div>
          <div class="ess-kpi-value">${formatCurrency(rev)}</div>
          <div class="ess-kpi-meta">Gross Margin: ${params.dailySales?.profitMarginPct ?? 0}% (${formatCurrency(params.dailySales?.grossProfitCents ?? 0)})</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Completed Orders</div>
          <div class="ess-kpi-value">${txCount}</div>
          <div class="ess-kpi-meta">Avg Transaction: ${formatCurrency(avgTxCents)}</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Active / Pending Orders</div>
          <div class="ess-kpi-value">${pendingOrders.length}</div>
          <div class="ess-kpi-meta">${pendingApprovals.length} restricted approvals waiting</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Low-Stock Alerts</div>
          <div class="ess-kpi-value" style="color:${lowStockItems.length > 0 ? 'var(--ess-warning-text)' : 'inherit'};">${lowStockItems.length}</div>
          <div class="ess-kpi-meta">Tracked SKUs needing replenishment</div>
        </div>
      </div>

      <!-- Sales Performance Chart & Payment Breakdown -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Hourly &amp; Shift Sales Performance</h3>
            <span class="ess-badge ess-badge-neutral">Today (${escapeHtml(params.dailySales?.date || 'Live')})</span>
          </div>
          <div class="ess-chart-bars">
            ${[
              { label: '08:00 - 11:00 (Breakfast)', pct: 28, amount: Math.round(rev * 0.18) },
              { label: '11:00 - 14:00 (Lunch Peak)', pct: 82, amount: Math.round(rev * 0.42) },
              { label: '14:00 - 17:00 (Afternoon)', pct: 35, amount: Math.round(rev * 0.15) },
              { label: '17:00 - 21:00 (Dinner Grill)', pct: 68, amount: Math.round(rev * 0.25) },
            ]
              .map(
                (bar) => `
              <div class="ess-chart-row">
                <div class="ess-chart-label">
                  <span>${bar.label}</span>
                  <strong>${formatCurrency(bar.amount)}</strong>
                </div>
                <div class="ess-chart-track">
                  <div class="ess-chart-fill" style="width:${bar.pct}%"></div>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Sales by Payment Method &amp; COGS</h3>
            <span class="ess-badge ess-badge-orange">Verified</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row"><span>Cash Drawer Revenue</span><strong>${formatCurrency(cashCents)}</strong></div>
            <div class="ess-kv-row"><span>M-Pesa Mobile Money</span><strong style="color:var(--ess-primary);">${formatCurrency(mpesaCents)}</strong></div>
            <div class="ess-kv-row"><span>Card / Split Settlements</span><strong>${formatCurrency(otherCents)}</strong></div>
            <div class="ess-kv-row"><span>Cost of Goods Sold (COGS)</span><span>${formatCurrency(params.dailySales?.totalCostCents ?? 0)}</span></div>
            <div class="ess-kv-row" style="border-top:1px solid var(--ess-border);padding-top:8px;">
              <strong>Net Gross Profit</strong>
              <strong style="color:var(--ess-success-text);font-size:16px;">${formatCurrency(params.dailySales?.grossProfitCents ?? 0)}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Low Stock Alerts & Recent Staff Activity -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Low-Stock Inventory Alerts</h3>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="mgr-go-inv">Inventory Report</button>
          </div>
          <div class="ess-stack-sm">
            ${lowStockItems
              .slice(0, 5)
              .map(
                (b) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(b.product_name)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">SKU: ${escapeHtml(b.variant_sku)} &bull; ${escapeHtml(b.location_name)}</div>
                </div>
                <span class="ess-badge ${Number(b.quantity_on_hand) <= 0 ? 'ess-badge-danger' : 'ess-badge-warning'}">
                  ${Number(b.quantity_on_hand)} left
                </span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Recent Staff &amp; Floor Activity</h3>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="mgr-go-audit">Full Audit Log</button>
          </div>
          <div class="ess-stack-sm">
            ${params.auditLogs
              .slice(0, 5)
              .map(
                (log) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(log.actor)} (${escapeHtml(log.role)})</strong>
                  <div style="font-size:12px;color:var(--ess-text-secondary);">${escapeHtml(log.target)}</div>
                </div>
                <span class="ess-badge ess-badge-neutral">${escapeHtml(log.action)}</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#mgr-go-approvals')?.addEventListener('click', () => params.onNavigateTab('approvals'));
  viewport.querySelector('#mgr-go-reports')?.addEventListener('click', () => params.onNavigateTab('orders-sales'));
  viewport.querySelector('#mgr-go-inv')?.addEventListener('click', () => params.onNavigateTab('inventory-reports'));
  viewport.querySelector('#mgr-go-audit')?.addEventListener('click', () => params.onNavigateTab('staff-audit'));
}

// ============================================================================
// 2. DISCOUNTS, VOIDS & REFUNDS APPROVALS
// ============================================================================

function renderManagerApprovalsScreen(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    managerName: string;
    approvals: any[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Discounts, Void Requests &amp; Refund Approvals</h2>
          <p class="ess-section-desc">Cashiers and Waiters cannot approve their own restricted actions &mdash; review and authorize below</p>
        </div>
      </div>

      <div class="ess-card">
        ${
          params.approvals.length === 0
            ? renderEmptyState({
                title: 'No Approval Requests',
                description: 'All discount, order cancellation, and void requests have been resolved.',
              })
            : `
          <div class="ess-table-wrap">
            <table class="ess-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Ref Code</th>
                  <th>Requested By</th>
                  <th>Value</th>
                  <th>Reason Provided</th>
                  <th>Status</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                ${params.approvals
                  .map(
                    (a) => `
                  <tr>
                    <td><span class="ess-badge ess-badge-orange">${escapeHtml(a.type)}</span></td>
                    <td><strong>${escapeHtml(a.referenceCode)}</strong></td>
                    <td>${escapeHtml(a.requestedBy)} (${escapeHtml(a.requesterRole)})</td>
                    <td><strong>${formatCurrency(a.amountCents)}</strong></td>
                    <td>${escapeHtml(a.reason)}</td>
                    <td>
                      <span class="ess-badge ${
                        a.status === 'APPROVED'
                          ? 'ess-badge-success'
                          : a.status === 'REJECTED'
                          ? 'ess-badge-danger'
                          : 'ess-badge-warning'
                      }">${escapeHtml(a.status)}</span>
                    </td>
                    <td>
                      ${
                        a.status === 'PENDING'
                          ? `
                        <div class="ess-btn-group">
                          <button type="button" class="ess-btn ess-btn-primary ess-btn-sm" data-apr-id="${escapeHtml(a.id)}" data-decision="APPROVED">
                            Approve
                          </button>
                          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-apr-id="${escapeHtml(a.id)}" data-decision="REJECTED">
                            Reject
                          </button>
                        </div>
                      `
                          : `<span style="font-size:12px;color:var(--ess-text-muted);">By ${escapeHtml(a.resolvedBy || 'Manager')}</span>`
                      }
                    </td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        `
        }
      </div>
    </div>
  `;

  viewport.querySelectorAll('[data-apr-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-apr-id') || '';
      const decision = btn.getAttribute('data-decision') as 'APPROVED' | 'REJECTED';
      restaurantMockAdapter.resolveApproval(params.tenantId, id, decision, params.managerName);
      showToast({
        title: `Request ${decision}`,
        message: `Audit trail recorded under ${params.managerName}.`,
        type: decision === 'APPROVED' ? 'success' : 'info',
      });
      params.onRefresh();
    });
  });
}

// ============================================================================
// 3. SALES ANALYTICS, ORDER MANAGEMENT & DAILY/MONTHLY REPORTS
// ============================================================================

function renderOrdersAndSalesAnalyticsScreen(
  viewport: HTMLElement,
  params: {
    dailySales: DailySalesReport;
    sales: SaleRecord[];
    orders: any[];
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Sales Analytics, Order Management &amp; Financial Reports</h2>
          <p class="ess-section-desc">Authoritative revenue, Cost of Goods Sold (COGS), gross margin &amp; active dine-in orders</p>
        </div>
        <button type="button" class="ess-btn ess-btn-primary" id="mgr-export-daily-csv">
          Export Daily &amp; Monthly Financial Report (CSV)
        </button>
      </div>

      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Gross Revenue</div>
          <div class="ess-kpi-value">${formatCurrency(params.dailySales?.totalRevenueCents ?? 0)}</div>
          <div class="ess-kpi-meta">${params.dailySales?.transactionCount ?? 0} settled receipts</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Cost of Goods Sold (COGS)</div>
          <div class="ess-kpi-value">${formatCurrency(params.dailySales?.totalCostCents ?? 0)}</div>
          <div class="ess-kpi-meta">Calculated from unit cost snapshots</div>
        </div>
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Gross Profit</div>
          <div class="ess-kpi-value">${formatCurrency(params.dailySales?.grossProfitCents ?? 0)}</div>
          <div class="ess-kpi-meta">Margin: ${params.dailySales?.profitMarginPct ?? 0}%</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Discounts Granted</div>
          <div class="ess-kpi-value">${formatCurrency(params.dailySales?.totalDiscountCents ?? 0)}</div>
          <div class="ess-kpi-meta">Audited line-item discounts</div>
        </div>
      </div>

      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Live Restaurant Order Management</h3>
        </div>
        <div class="ess-table-wrap">
          <table class="ess-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Table</th>
                <th>Waiter</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${params.orders
                .map(
                  (o) => `
                <tr>
                  <td><strong>${escapeHtml(o.orderNumber)}</strong></td>
                  <td>${escapeHtml(o.tableName)}</td>
                  <td>${escapeHtml(o.waiterName)}</td>
                  <td>${o.items.map((i: any) => `${i.quantity}x ${i.productName}`).join(', ')}</td>
                  <td><strong>${formatCurrency(o.totalCents)}</strong></td>
                  <td><span class="ess-badge ess-badge-orange">${escapeHtml(o.status)}</span></td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#mgr-export-daily-csv')?.addEventListener('click', () => {
    exportRowsToCsv(
      `ess-manager-financial-report-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Receipt Number', 'Timestamp', 'Cashier', 'Payment Method', 'Subtotal (KES)', 'Discount (KES)', 'Total (KES)', 'Status'],
      params.sales.map((s) => [
        s.receipt_number,
        s.created_at,
        s.cashier_name || 'Cashier',
        s.payment_method,
        (s.subtotal_cents / 100).toFixed(2),
        (s.discount_cents / 100).toFixed(2),
        (s.total_cents / 100).toFixed(2),
        s.status,
      ])
    );
  });
}

// ============================================================================
// 4. CASHIER & WAITER PERFORMANCE
// ============================================================================

function renderStaffPerformanceScreen(
  viewport: HTMLElement,
  params: {
    staff: any[];
    sales: SaleRecord[];
    orders: any[];
  }
) {
  const totalSalesCents = params.sales.reduce((s, r) => s + r.total_cents, 0);
  const waiterOrdersTotal = params.orders.reduce((s, o) => s + o.totalCents, 0);

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Cashier &amp; Waiter Performance Metrics</h2>
          <p class="ess-section-desc">Track cashier checkout speed, shift drawer accuracy &amp; waiter table turnover</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Cashier Performance</h3>
            <span class="ess-badge ess-badge-success">Drawer Balanced</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row">
              <span>Lead Cashier</span>
              <strong>Grace Wanjiku (cashier@savannah.co.ke)</strong>
            </div>
            <div class="ess-kv-row">
              <span>Completed POS Checkouts</span>
              <strong>${params.sales.length} Transactions</strong>
            </div>
            <div class="ess-kv-row">
              <span>Total Settled Value</span>
              <strong>${formatCurrency(totalSalesCents)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>Cash Drawer Variance</span>
              <span class="ess-badge ess-badge-success">KES 0.00 Variance</span>
            </div>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Waiter Floor Performance</h3>
            <span class="ess-badge ess-badge-orange">Active Shift</span>
          </div>
          <div class="ess-stack-sm">
            <div class="ess-kv-row">
              <span>Lead Waiter</span>
              <strong>Kevin Omondi (waiter@savannah.co.ke)</strong>
            </div>
            <div class="ess-kv-row">
              <span>Kitchen Orders Submitted</span>
              <strong>${params.orders.length} Table Orders</strong>
            </div>
            <div class="ess-kv-row">
              <span>Total Dine-In Order Value</span>
              <strong>${formatCurrency(waiterOrdersTotal)}</strong>
            </div>
            <div class="ess-kv-row">
              <span>Average Table Service Time</span>
              <strong>14 mins (Kitchen to Table)</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// 5. INVENTORY VALUATION & PRODUCT PERFORMANCE REPORTS
// ============================================================================

function renderInventoryAndProductReportsScreen(
  viewport: HTMLElement,
  params: {
    valuation: InventoryValuationReport;
    balances: InventoryBalanceRecord[];
    products: ProductRecord[];
    sales: SaleRecord[];
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Inventory Valuation &amp; Product Performance</h2>
          <p class="ess-section-desc">Live stock valuation at cost vs retail and top-performing menu items</p>
        </div>
      </div>

      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Total Stock Units</div>
          <div class="ess-kpi-value">${params.valuation?.totalStockUnits ?? 0}</div>
          <div class="ess-kpi-meta">Across all tenant locations</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Inventory Cost Value</div>
          <div class="ess-kpi-value">${formatCurrency(params.valuation?.totalCostValueCents ?? 0)}</div>
          <div class="ess-kpi-meta">Capital tied in stock</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Inventory Retail Value</div>
          <div class="ess-kpi-value">${formatCurrency(params.valuation?.totalRetailValueCents ?? 0)}</div>
          <div class="ess-kpi-meta">Projected menu revenue</div>
        </div>
        <div class="ess-card ess-kpi-card ess-kpi-highlight">
          <div class="ess-kpi-label">Potential Gross Profit</div>
          <div class="ess-kpi-value">${formatCurrency(params.valuation?.potentialProfitCents ?? 0)}</div>
          <div class="ess-kpi-meta">Projected Margin: ${params.valuation?.potentialMarginPct ?? 0}%</div>
        </div>
      </div>

      <div class="ess-card">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Product Margin &amp; Stock Performance</h3>
        </div>
        <div class="ess-table-wrap">
          <table class="ess-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Menu Item</th>
                <th>Unit Cost</th>
                <th>Selling Price</th>
                <th>Unit Margin</th>
                <th>Available Stock</th>
              </tr>
            </thead>
            <tbody>
              ${params.products
                .map((p) => {
                  const v = p.variants[0];
                  if (!v) return '';
                  const margin = v.retail_price_cents - v.cost_price_cents;
                  const marginPct =
                    v.retail_price_cents > 0
                      ? Math.round((margin / v.retail_price_cents) * 100)
                      : 0;
                  return `
                    <tr>
                      <td><strong>${escapeHtml(v.sku)}</strong></td>
                      <td>${escapeHtml(p.name)}</td>
                      <td>${formatCurrency(v.cost_price_cents)}</td>
                      <td><strong>${formatCurrency(v.retail_price_cents)}</strong></td>
                      <td><span class="ess-badge ess-badge-success">${formatCurrency(margin)} (${marginPct}%)</span></td>
                      <td><strong>${Number(v.total_stock ?? 0)}</strong></td>
                    </tr>
                  `;
                })
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

// ============================================================================
// 6. STAFF MANAGEMENT & AUDIT HISTORY
// ============================================================================

function renderStaffAndAuditScreen(
  viewport: HTMLElement,
  params: {
    staff: any[];
    branches: BranchRecord[];
    auditLogs: any[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Restaurant Staff Management &amp; Security Audit Log</h2>
          <p class="ess-section-desc">Provision role-based employee accounts and inspect traceable operational actions</p>
        </div>
        <button type="button" class="ess-btn ess-btn-primary" id="mgr-add-staff-btn">
          + Add Restaurant Employee
        </button>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Active Restaurant Employees (${params.staff.length})</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.staff
              .map(
                (u) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(u.firstName)} ${escapeHtml(u.lastName)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">${escapeHtml(u.email)}</div>
                </div>
                <div>
                  ${(u.roles || [])
                    .map((r: any) => `<span class="ess-badge ess-badge-orange" style="margin-left:4px;">${escapeHtml(r.role)}</span>`)
                    .join('')}
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Traceable Security &amp; Operational Audit History</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.auditLogs
              .map(
                (l) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(l.action)}</strong> &bull; <span style="font-size:12px;">${escapeHtml(l.actor)} (${escapeHtml(l.role)})</span>
                  <div style="font-size:12px;color:var(--ess-text-muted);">${escapeHtml(l.target)}</div>
                </div>
                <span style="font-size:11px;color:var(--ess-text-muted);">${formatDateTime(l.timestamp)}</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#mgr-add-staff-btn')?.addEventListener('click', () => {
    openAddStaffModal(params.branches, params.onRefresh);
  });
}

export function openAddStaffModal(branches: BranchRecord[], onSuccess: () => void) {
  openModal({
    title: 'Add Restaurant Staff Account',
    subtitle: 'Assigns strict RBAC workspace permissions on the backend',
    width: 'md',
    bodyHtml: `
      <form class="ess-form">
        <div class="ess-grid-2">
          <div class="ess-form-group"><label class="ess-label">First Name *</label><input type="text" id="stf-first" class="ess-input" required /></div>
          <div class="ess-form-group"><label class="ess-label">Last Name *</label><input type="text" id="stf-last" class="ess-input" required /></div>
        </div>
        <div class="ess-grid-2">
          <div class="ess-form-group"><label class="ess-label">Work Email *</label><input type="email" id="stf-email" class="ess-input" placeholder="staff@savannah.co.ke" required /></div>
          <div class="ess-form-group"><label class="ess-label">Phone Number</label><input type="tel" id="stf-phone" class="ess-input" placeholder="2547XXXXXXXX" /></div>
        </div>
        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Assigned Workspace Role *</label>
            <select id="stf-role" class="ess-select">
              <option value="CASHIER">CASHIER (Cashier Workspace)</option>
              <option value="WAITER">WAITER (Waiter Workspace)</option>
              <option value="INVENTORY_CLERK">INVENTORY_CLERK (Stock Keeper Workspace)</option>
              <option value="MANAGER">MANAGER (Manager Workspace)</option>
            </select>
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Assigned Branch</label>
            <select id="stf-branch" class="ess-select">
              ${branches.map((b) => `<option value="${escapeHtml(b.id)}">${escapeHtml(b.name)}</option>`).join('')}
            </select>
          </div>
        </div>
        <div class="ess-grid-2">
          <div class="ess-form-group">
            <label class="ess-label">Initial Password (Min 8 chars) *</label>
            <input type="password" id="stf-pass" class="ess-input" value="Password123!" required />
          </div>
          <div class="ess-form-group">
            <label class="ess-label">Fast Login PIN (4 Digits) *</label>
            <input type="text" id="stf-pin" class="ess-input" maxlength="6" placeholder="e.g. 1234" value="1234" required />
            <span class="ess-field-hint">Used for 1-second PIN Pad login on restaurant POS terminals.</span>
          </div>
        </div>
      </form>
    `,
    footerHtml: `
      <button type="button" class="ess-btn ess-btn-secondary" id="stf-cancel">Cancel</button>
      <button type="button" class="ess-btn ess-btn-primary" id="stf-save">Provision Employee Account</button>
    `,
    onMount: (root, close) => {
      root.querySelector('#stf-cancel')?.addEventListener('click', close);
      root.querySelector('#stf-save')?.addEventListener('click', async () => {
        const firstName = (root.querySelector('#stf-first') as HTMLInputElement).value.trim();
        const lastName = (root.querySelector('#stf-last') as HTMLInputElement).value.trim();
        const email = (root.querySelector('#stf-email') as HTMLInputElement).value.trim();
        const phone = (root.querySelector('#stf-phone') as HTMLInputElement).value.trim();
        const role = (root.querySelector('#stf-role') as HTMLSelectElement).value as any;
        const branchId = (root.querySelector('#stf-branch') as HTMLSelectElement).value;
        const password = (root.querySelector('#stf-pass') as HTMLInputElement).value;
        const pin = (root.querySelector('#stf-pin') as HTMLInputElement)?.value.trim() || '1234';

        if (!firstName || !lastName || !email || password.length < 8) {
          showToast({ title: 'Validation Error', message: 'Please complete all required fields.', type: 'warning' });
          return;
        }
        try {
          await apiClient.addStaffMember({
            firstName,
            lastName,
            email,
            phone: phone || undefined,
            role,
            branchId: branchId || undefined,
            password,
            pin,
          });
          close();
          showToast({ title: 'Staff Account Created', message: `${firstName} ${lastName} assigned ${role}`, type: 'success' });
          onSuccess();
        } catch (err: any) {
          showToast({ title: 'Could Not Add Staff', message: err?.message || 'Error creating staff member', type: 'danger' });
        }
      });
    },
  });
}

// ============================================================================
// 9. PRIVATE MANAGER PROFILE & PIN POWER
// ============================================================================

function renderManagerProfileScreen(
  viewport: HTMLElement,
  params: {
    dailySales: any;
    sales: any[];
    staff: any[];
    branches: any[];
    onRefresh: () => void;
    onNavigateTab: (tab: string) => void;
  }
) {
  const user = apiClient.getUser();
  const tenant = apiClient.getTenant();
  const tenantId = apiClient.getEffectiveTenantId();
  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Restaurant Manager';

  viewport.innerHTML = `
    <div class="ess-stack-md">
      <div class="ess-card">
        <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;">
          <div style="display:flex;align-items:center;gap:16px;">
            <div style="width:68px;height:68px;border-radius:50%;background:var(--ess-primary,#2563eb);color:#fff;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:700;">
              ${escapeHtml((user?.firstName?.[0] || 'M') + (user?.lastName?.[0] || ''))}
            </div>
            <div>
              <div style="display:flex;align-items:center;gap:8px;">
                <h2 style="margin:0;font-size:22px;font-weight:800;color:var(--ess-text-primary);">${escapeHtml(fullName)}</h2>
                <span class="ess-role-pill" style="font-size:11px;">MANAGER</span>
              </div>
              <div style="font-size:13px;color:var(--ess-text-secondary);margin-top:2px;">
                ${escapeHtml(user?.email || '')} &bull; ${escapeHtml(tenant?.name || 'Restaurant Management')}
              </div>
              <div style="font-size:12px;color:var(--ess-success-text);margin-top:4px;font-weight:600;">
                ● Private Session Protected with 4-Digit Fast Login PIN
              </div>
            </div>
          </div>

          <div style="display:flex;gap:10px;">
            <button type="button" class="ess-btn ess-btn-secondary" id="mgr-lock-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              🔒 Lock Station Now
            </button>
            <button type="button" class="ess-btn ess-btn-primary" id="mgr-switch-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              👤 Switch Staff
            </button>
          </div>
        </div>
      </div>

      <!-- Manager Summary Grid -->
      <div class="ess-grid-4">
        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Today's Gross Sales</div>
          <div class="ess-metric-value" style="color:var(--ess-primary);">${formatCurrency(params.dailySales?.total_sales_cents || 0)}</div>
          <div class="ess-metric-sub">Combined restaurant orders</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Completed Transactions</div>
          <div class="ess-metric-value">${params.dailySales?.completed_transactions_count || 0}</div>
          <div class="ess-metric-sub">Settled sales records</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Active Staff</div>
          <div class="ess-metric-value">${params.staff.length}</div>
          <div class="ess-metric-sub">Cashiers, Waiters & Stock Keepers</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Restaurant Branches</div>
          <div class="ess-metric-value">${params.branches.length}</div>
          <div class="ess-metric-sub">Active operational locations</div>
        </div>
      </div>

      <!-- PIN Management & Security -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🔑 My Secret Fast-Login PIN
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 16px 0;">
            Your personal 4 to 6-digit numeric PIN authorizes manager overrides, discount approvals, and workspace unlock.
          </p>

          <form id="mgr-pin-form" style="display:flex;flex-direction:column;gap:12px;">
            <div class="ess-form-group">
              <label class="ess-label">New 4 to 6-Digit PIN *</label>
              <input type="password" id="mgr-new-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="e.g. 9152" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div class="ess-form-group">
              <label class="ess-label">Confirm New PIN *</label>
              <input type="password" id="mgr-confirm-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="Re-enter PIN" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div id="mgr-pin-error" style="color:var(--ess-danger,#ef4444);font-size:12px;font-weight:600;min-height:16px;"></div>

            <div>
              <button type="submit" class="ess-btn ess-btn-primary" id="mgr-save-pin-btn" style="font-weight:700;">
                Save My Secret PIN
              </button>
            </div>
          </form>
        </div>

        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🛡️ Manager Authority &amp; Access Controls
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 14px 0;">
            Manager controls and permissions:
          </p>
          <ul style="margin:0;padding-left:18px;font-size:13px;color:var(--ess-text-primary);line-height:1.6;">
            <li><strong>Approvals &amp; Voids:</strong> Your PIN is requested on floor terminals when approving discounts or voids.</li>
            <li><strong>Private Manager Space:</strong> Waiters, cashiers, and stock keepers cannot enter this workspace without entering your PIN.</li>
            <li><strong>Terminal Lock:</strong> Always lock station when stepping out onto the floor.</li>
          </ul>

          <div style="margin-top:20px;padding:12px;background:var(--ess-bg-subtle,#f8fafc);border-radius:6px;border:1px solid var(--ess-border);">
            <div style="font-size:12px;font-weight:700;color:var(--ess-text-primary);">Quick Actions:</div>
            <div style="display:flex;gap:8px;margin-top:8px;">
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="mgr-go-ov">Business Overview</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="mgr-go-app">Approvals Queue</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="mgr-go-stf">Staff Performance</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Bind actions
  viewport.querySelector('#mgr-lock-btn')?.addEventListener('click', () => {
    openLockStationModal(user, tenantId);
  });

  viewport.querySelector('#mgr-switch-btn')?.addEventListener('click', () => {
    openFastSwitchStaffModal(tenantId);
  });

  viewport.querySelector('#mgr-go-ov')?.addEventListener('click', () => params.onNavigateTab('overview'));
  viewport.querySelector('#mgr-go-app')?.addEventListener('click', () => params.onNavigateTab('approvals'));
  viewport.querySelector('#mgr-go-stf')?.addEventListener('click', () => params.onNavigateTab('staff-performance'));

  // Bind PIN change form
  const pinForm = viewport.querySelector('#mgr-pin-form') as HTMLFormElement;
  pinForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const newPin = (viewport.querySelector('#mgr-new-pin') as HTMLInputElement)?.value.trim();
    const confirmPin = (viewport.querySelector('#mgr-confirm-pin') as HTMLInputElement)?.value.trim();
    const errDiv = viewport.querySelector('#mgr-pin-error') as HTMLElement;
    const saveBtn = viewport.querySelector('#mgr-save-pin-btn') as HTMLButtonElement;

    if (!newPin || newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      if (errDiv) errDiv.textContent = 'PIN must be 4 to 6 numeric digits';
      return;
    }
    if (newPin !== confirmPin) {
      if (errDiv) errDiv.textContent = 'PINs do not match. Please verify.';
      return;
    }

    if (errDiv) errDiv.textContent = '';
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving PIN...';
    }

    try {
      await apiClient.updateStaffPin(newPin);
      showToast({
        title: 'Secret PIN Updated',
        message: 'Your personal fast-login PIN is now active for this station.',
        type: 'success',
      });
      (viewport.querySelector('#mgr-new-pin') as HTMLInputElement).value = '';
      (viewport.querySelector('#mgr-confirm-pin') as HTMLInputElement).value = '';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    } catch (err: any) {
      if (errDiv) errDiv.textContent = err?.message || 'Failed to update PIN.';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    }
  });
}


